import { Utils } from 'Shared/utils';
import { rateLimiter } from 'Shared/utils/throttle';
import { delegate } from 'Shared/utils/delegate';

{
   /**
    * Simple dropdown that toggles .content-dropdown-expanded on an
    * .js-content-dropdown-container when its
    * .js-content-dropdown-toggle is clicked, and closes when clicking outside of
    * the dropdown.
    *
    * groupEl: If you pass in a container with multiple
    * .js-content-dropdown-containers in it, this will only allow one dropdown in
    * the group to be open at any given time.
    *
    * .js-content-dropdown-close is for a button that always closes the dropdown,
    * and is an element inside of the dropdown's contents.
    *
    * .content-dropdown-notification is removed as soon as the user interacts with
    * the dropdown once.
    *
    * If the container has overflow:visible, clicking outside of the container
    * will close the dropdown. If overflow:hidden, we assume that the dropdown
    * height pushes other content down, so it will stay open.
    *
    * Use .content-dropdown-default on .js-content-dropdown-container for a
    * default theme.
    *
    * groupEl: one element, or anything iterable of elements (array, NodeList).
    */

   let defaults = {
      toggle: 'js-content-dropdown-toggle',
      close: 'js-content-dropdown-close',
      container: 'js-content-dropdown-container',
      cssExpanded: 'content-dropdown-expanded',
      cssNotification: 'content-dropdown-notification',
      forceCloseOpenedDropdown: true,
   };

   window.ContentDropdownGroup = function (groupEl, options) {
      options = { ...defaults, ...options };

      let openedDropdown = null;
      let originalDropdownPosition;

      const dissolve = el => {
         el.classList.add('transition-opacity', 'duration-100');
         el.classList.remove('opacity-100');
         el.classList.add('opacity-0');
         setTimeout(() => {
            el.style.display = 'none';
            el.classList.remove('opacity-0');
         }, 100);
      };

      const reveal = el => {
         el.classList.add('opacity-0', 'transition-opacity', 'duration-100');
         el.style.display = 'block';

         requestAnimationFrame(() => {
            el.classList.remove('opacity-0');
            el.classList.add('opacity-100');
         });
      };

      let closeDropdown = function () {
         if (openedDropdown !== null) {
            openedDropdown.classList.remove(options.cssExpanded);
            openedDropdown
               .querySelectorAll(':scope > .content-dropdown-list')
               .forEach(el => dissolve(el));
            document.removeEventListener('scroll', dropdownHider);
            openedDropdown = null;
         }
      };

      // If they clicked anywhere in the document outside of the element,
      // then close it. Don't close it if overflow:hidden, because closing
      // it would change the whole document's height.
      let closeOpenedDropdown = function (ev, el) {
         if (
            options.forceCloseOpenedDropdown &&
            openedDropdown &&
            ev.target &&
            !openedDropdown.contains(ev.target) &&
            !doesDropdownAffectHeight(openedDropdown)
         ) {
            closeDropdown();
         }
      };

      let toggleDropdown = function (ev, el) {
         let container = el.parentElement.closest('.' + options.container);

         if (container.classList.contains(options.cssExpanded)) {
            // Close this item if it's already open.
            closeDropdown();
         } else {
            // Otherwise, open this item.
            closeDropdown(); // Close any other open items in this group first.
            // Clear any notifications because they've interacted with it
            container.classList.remove(options.cssNotification);

            // Open the item and save a reference to it so we can close it later.
            container.classList.add(options.cssExpanded);
            openedDropdown = container;
            container.querySelectorAll(':scope > .content-dropdown-list').forEach(el => reveal(el));

            // Closes open menus when the user scrolls down and is not mobile.
            // Measure how much scrolling happened:
            document.addEventListener('scroll', dropdownHider);
            originalDropdownPosition = window.scrollY;
         }
      };

      // eslint-disable-next-line no-var
      var doesDropdownAffectHeight = function (el) {
         return getComputedStyle(el).overflow != 'visible';
      };

      // Watch how much scrolling happened and close the dropdown do something if
      // the threshold was hit.
      // eslint-disable-next-line no-var
      var dropdownHider = rateLimiter(250, () => {
         // Scrolled up or down.
         if (
            Math.abs(originalDropdownPosition - window.scrollY) > 100 && // Close the dropdown, but only on non-mobile views.
            openedDropdown !== null &&
            !doesDropdownAffectHeight(openedDropdown)
         ) {
            closeDropdown();
         }
      });

      window.addEventListener('click', closeOpenedDropdown);

      const groups = groupEl instanceof Element ? [groupEl] : Array.from(groupEl);
      groups.forEach(group => {
         delegate(group, 'click', '.' + options.toggle, toggleDropdown);
         delegate(group, 'click', '.' + options.close, closeDropdown);
      });
   };
}
